import React, { useCallback, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import { KeyboardDatePickerProps } from '@material-ui/pickers';
import { useDateTimeFormat } from '@utmost.co/utmost-design-system';
import { ComponentPropBase } from '../types';
import { errorValue } from '../atoms/types';
import { objectUtil } from '../forms/utils/objectUtil';
import { DateTimePickerAtom } from '../atoms/DateTimePickerAtom/DateTimePickerAtom';
import { MatchHintSlot } from './MatchHintSlot';

type Props = ComponentPropBase &
  Partial<Omit<KeyboardDatePickerProps, 'onChange'>> & {
    placeholder?: string;
    mapInputValue: (value: any) => any;
    onChange?: (e: React.ChangeEvent<any>, setValue: (value: any) => void) => void;
  };

export const DateTimePicker: React.FC<Props> = ({
  label,
  placeholder,
  errors,
  helpText,
  touched,
  type,
  disabled,
  required,
  mapInputValue,
  onChange,
  onBlur,
  name,
  ...rest
}) => {
  const [input, meta, helpers] = useField(name || 'INVALID');
  const formikContext = useFormikContext();
  const [error, setError] = useState<string | React.ReactNode | null>(null);
  const { dateTimeFormat } = useDateTimeFormat();
  const fieldError = error || errorValue(meta.error) || errorValue(objectUtil.getIn(errors, name));

  const handleOnChange = useCallback(
    (date: any) => {
      helpers.setTouched(true);
      setError(null);
      if (onChange) {
        // @ts-ignore: TODO SD-11228
        onChange(date, helpers.setValue);
      } else {
        helpers.setValue(date);
      }
    },
    [helpers, onChange]
  );

  const handleOnBlur = useCallback(
    (event: any) => {
      if (onBlur) {
        onBlur(event);
      }
      helpers.setTouched(true);
      input.onBlur(event);
    },
    [helpers, input, onBlur]
  );

  const hasSubmitted = formikContext.submitCount > 0;

  return (
    <div data-test={`${name}-field-control`} className="field-control">
      <MatchHintSlot name={name}>
        <DateTimePickerAtom
          id={input.name}
          {...rest}
          format={`${dateTimeFormat.short} ${dateTimeFormat.time}`}
          data-test={`${name}-field`}
          placeholder={placeholder}
          value={mapInputValue ? mapInputValue(input.value) : (input.value ?? null)}
          onChange={handleOnChange}
          onBlur={handleOnBlur}
          required={required}
          name={name}
          label={label}
          helperText={(hasSubmitted || meta.touched) && fieldError ? fieldError : helpText}
          disabled={disabled}
          error={(hasSubmitted || meta.touched) && !!fieldError}
        />
      </MatchHintSlot>
    </div>
  );
};
